<template>
  <div
    class="sn-bs-wrapper"
    v-loading="loading"
    element-loading-background="rgba(0, 0, 0, 0.2)"
  >
    <div class="sn-bs-wrapper__header">
      <el-select
        size="mini"
        class="sn-bs-select"
        popper-class="sn-bs-select-dropdown"
        v-model="cropId"
        placeholder="请选择"
        filterable
        ref="cropSearch"
        @change="selectCrop"
      >
        <el-option
          v-for="(item, index) in vegetableVarietyList"
          :key="index"
          :label="item.ncCropName"
          :value="item.id"
        >
        </el-option>
      </el-select>
    </div>
    <main class="sn-bs-wrapper__main" v-show="!loading && hasData">
      <div class="sn-bs-wrapper__chart-container" ref="EContainer"></div>
    </main>
    <empty v-if="!loading && !hasData"></empty>
  </div>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

import Empty from "@/components/bs/BsBoxPanel/Empty";

import { crop, cropPrice } from "@/api/request/snkd.operate";
// import { farm } from "@/api/request/snkd.produce";

import { maxDecimalPlaces, getIntervalTime, formatDate } from "@/utils";
import { setStorage, getStorage } from "@/utils/storage";
import { LINE_OPTIONS$1 } from "@/confing/echarts";

const xData = [
  "1月",
  "2月",
  "3月",
  "4月",
  "5月",
  "6月",
  "7月",
  "8月",
  "9月",
  "10月",
  "11月",
  "12月",
];
// echarts 超级变量含义 {a} series.name, {b} data.name {c} data.value {d} data%
const lineOption = {
  ...LINE_OPTIONS$1,
  grid: {
    show: true,
    borderColor: "#0D394A",
    bottom: 25,
    top: 42,
    left: 35,
    right: 5,
    // height: '65%'
  },
  tooltip: {
    show: true,
    trigger: "axis",
    formatter(value) {
      const s = value.map((item) => {
        return item.value !== null
          ? `${item.seriesName}：${maxDecimalPlaces(item.value)}元/公斤`
          : "";
      });
      return `${value[0].axisValue}<br/>${s.join("<br />")}`;
    },
    backgroundColor: "#043250",
    padding: [13, 14, 13, 11],
    textStyle: {
      fontSize: 12,
      color: "#9ED2D8",
    },
  },
  legend: {
    show: true,
    type: "plain",
    textStyle: {
      color: "#6DC1CB",
      fontSize: 12,
    },
    backgroundColor: "rgba(255, 255, 255, 0)",
    top: 5,
    right: 0,
    itemWidth: 30,
    itemHeight: 10,
    height: 20,
    data: [
      {
        name: `${new Date().getFullYear() - 1}年`,
        icon: `image://${require("/src/assets/images/bs/prevYears.png")}`,
      },
      {
        name: `${new Date().getFullYear()}年`,
        icon: `image://${require("/src/assets/images/bs/curYears.png")}`,
      },
    ],
  },
  xAxis: {
    data: xData,
    boundaryGap: false,
    axisLabel: {
      textStyle: {
        color: "#6DC1CB",
        fontSize: 12,
      },
      align: "center",
    },
    axisTick: {
      show: false,
    },
    axisLine: {
      show: true,
      lineStyle: {
        // color: '#0D394A'
        color: "rgba(109,193,203,0.2)",
      },
    },
    z: 10,
  },
  yAxis: {
    name: "(元/公斤)",
    nameTextStyle: {
      fontSize: 12,
      color: "#6DC1CB",
      padding: [4, 8, 5, 35],
    },
    axisLine: {
      show: false,
      lineStyle: {
        color: "#0D394A",
      },
    },
    axisTick: {
      show: false,
    },
    splitLine: {
      show: true,
      lineStyle: {
        // color: '#0D394A'
        color: "rgba(109,193,203,0.2)",
      },
    },
    axisLabel: {
      textStyle: {
        color: "#6DC1CB",
        fontSize: 12,
      },
    },
  },
  series: [
    {
      data: [],
      type: "line",
      smooth: true,
      symbolSize: 0,
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: "rgba(0, 162, 255, 0.5)", // 0% 处的颜色
            },
            {
              offset: 1,
              color: "rgba(60, 105, 217, 0)", // 100% 处的颜色
            },
          ],
          global: false, // 缺省为 false
        },
      },
      lineStyle: {
        color: "#00FFFF",
      },
    },
    {
      data: [],
      type: "line",
      smooth: true,
      symbolSize: 0,
      areaStyle: {
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            {
              offset: 0,
              color: "rgba(212, 167, 84, 0.5)", // 0% 处的颜色
            },
            {
              offset: 1,
              color: "rgba(212, 167, 84, 0)", // 100% 处的颜色
            },
          ],
          global: false, // 缺省为 false
        },
      },
      lineStyle: {
        color: "#D4A452",
      },
    },
  ],
};

export default {
  name: "PriceTrend",
  components: { Empty },
  data() {
    return {
      loading: false,
      cropId: "",
      vegetableVarietyList: [],
      hasData: true,
      priceTrendChart: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initModule();
    });
    window.addEventListener("resize", this.refreshRender);
    this.$once("hook:beforeDestroy", () => {
      window.removeEventListener("resize", this.refreshRender);
    });
  },
  computed: {
    ...mapState(["companyId", "farmId"]),
    ...mapState("farmInfo", ["farmInfo"]),
    eContainerRef() {
      return this.$refs.EContainer;
    },
  },
  methods: {
    async initModule() {
      this.loading = true;
      const vegetableVariety = getStorage("vegetableVariety");
      if (
        this.checkCropReload() &&
        vegetableVariety &&
        vegetableVariety.length
      ) {
        this.vegetableVarietyList = vegetableVariety;
        this.cropId = vegetableVariety[0].id;

        this.loading = false;

        await this.$nextTick();

        this.priceTrendChart = this._initEcharts(
          this.eContainerRef,
          lineOption
        );

        this.getCropPriceTable(this.cropId);

        return;
      }
      const res = await crop.listService({
        areaId: this.farmInfo.provinceId,
        keyword: "",
        pageNo: 1,
        pageSize: 10000,
      });

      this.vegetableVarietyList = res.list;
      setStorage("cropTime", new Date().getTime());
      setStorage("vegetableVariety", this.vegetableVarietyList);

      this.cropId = this.vegetableVarietyList[0].id;

      this.loading = false;

      await this.$nextTick();

      this.priceTrendChart = this._initEcharts(this.eContainerRef, lineOption);

      this.getCropPriceTable(this.cropId);
    },
    _initEcharts(el, option) {
      const chart = echarts.init(el);
      chart.setOption(option);
      return chart;
    },

    // 检测是否需要重新加载作物数据
    checkCropReload() {
      let cropTime = getStorage("cropTime");
      if (!cropTime) return true;
      cropTime = Number(cropTime);
      const updateMonth = new Date(cropTime).getMonth();
      const updateTime = new Date(cropTime).setMonth(updateMonth + 1, 1);
      const curEarlyMonth = new Date().getTime();
      return updateTime >= curEarlyMonth;
    },

    // 获取作物价格走势 2020-01-02 10:22:11
    getCropPriceTable(cropId) {
      this.hasData = true;
      this.loading = true;
      // 今年的时间
      const curYear = new Date().getFullYear();
      const curMonth = new Date().getMonth() + 1;
      const curYearList = getIntervalTime({
        startTime: `${curYear}-01-01`,
        howMonth: curMonth,
        format: "yyyy-MM",
      });
      // 去年的时间
      const prevYear = curYear - 1;
      const prevYearList = getIntervalTime({
        startTime: `${prevYear}-01-01`,
        format: "yyyy-MM",
      });

      // 两条并发
      axios
        .all([
          cropPrice.listService({
            dateListStr: curYearList,
            nccropId: cropId,
            provinceId: this.farmInfo.provinceId,
          }),
          cropPrice.listService({
            dateListStr: prevYearList,
            nccropId: cropId,
            provinceId: this.farmInfo.provinceId,
          }),
        ])
        .then((acct) => {
          console.log("acct=========>", acct);
          if (acct) {
            const curRes = acct[0];
            const prevRes = acct[1];
            let curTotal = 0;
            let prevTotal = 0;

            // if (curRes.respCode !== 0 || prevRes.respCode !== 0) {
            //   throw curRes.respCode;
            // }
            curRes &&
              curRes.length &&
              curRes.forEach((item) => {
                curTotal += item.price;
              });

            prevRes &&
              prevRes.length &&
              prevRes.forEach((item) => {
                prevTotal += item.price;
              });

            // 是否有数据
            const ownDataFlag = curTotal !== 0 || prevTotal !== 0;

            if (ownDataFlag) {
              this.priceTrendChart.setOption({
                tooltip: {
                  show: true,
                },
                yAxis: {
                  show: true, // !true
                },
                xAxis: {
                  show: true,
                },
                series: [
                  {
                    name: formatDate(`${prevYear}-01-01`, "yyyy年"),
                    data: (() => {
                      return prevRes && prevRes.length
                        ? prevRes.map((item) => ({
                            name: item.ncCropName,
                            value: item.price,
                          }))
                        : [];
                    })(),
                    symbol: "circle",
                    symbolSize: 2,
                    color: "#00F6FF",
                    // showAllSymbol: true
                  },
                  {
                    name: formatDate(`${curYear}-01-01`, "yyyy年"),
                    data: (() => {
                      return curRes && curRes.length
                        ? curRes.map((item) => ({
                            name: item.ncCropName,
                            value: item.price,
                          }))
                        : [];
                    })(),
                    symbol: "circle",
                    symbolSize: 2,
                    color: "#FBC02D",
                    // showAllSymbol: true
                  },
                ],
              });
            } else {
              this.hasData = false;
            }
          } else {
            this.hasData = false;
          }
          this.loading = false;
        })
        .catch((e) => {
          this.loading = false;
          this.hasData = false;
          throw e;
        });
    },
    selectCrop() {
      this.getCropPriceTable(this.cropId);
    },
    refreshRender() {
      if (this.priceTrendChart) {
        this.priceTrendChart.resize();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include bs(wrapper) {
  @include utils-full-box();
  position: relative;

  @include e(header) {
    position: absolute;
    top: -32px;
    right: 0px;
    width: 90px;
  }

  @include e(main) {
    @include utils-full-box();

    @include e(chart-container) {
      @include utils-full-box();
    }
  }
}
</style>
